import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Gift, Search, Plus } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

const STATUS_COLOR = {
  active:    { bg:'#dcfce7', fg:'#166534' },
  redeemed:  { bg:'#e0e7ff', fg:'#3730a3' },
  expired:   { bg:'#fee2e2', fg:'#991b1b' },
  cancelled: { bg:'#f3f4f6', fg:'#374151' },
};

const GiftCardsPage = ({ API_BASE }) => {
  const [cards, setCards] = useState([]);
  const [filter, setFilter] = useState('all');
  const [patients, setPatients] = useState([]);
  const [creating, setCreating] = useState(null);
  const [lookupCode, setLookupCode] = useState('');
  const [lookupResult, setLookupResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/gift-cards`, filter === 'all' ? {} : { params: { status: filter } });
      setCards(r.data || []);
    } catch (e) { alert('Load failed: ' + (e.response?.data?.error || e.message)); }
  }, [API_BASE, filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { axios.get(`${API_BASE}/patients`).then(r => setPatients(r.data || [])); }, [API_BASE]);

  const issue = async () => {
    if (!creating.faceValue) return alert('Amount required');
    try {
      await axios.post(`${API_BASE}/gift-cards`, { ...creating, createdBy: 'admin', sendWhatsApp: !!creating.issuedToPhone });
      setCreating(null); load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const lookup = async () => {
    if (!lookupCode) return;
    try {
      const r = await axios.get(`${API_BASE}/gift-cards/code/${lookupCode.toUpperCase()}`);
      setLookupResult(r.data);
    } catch (e) {
      setLookupResult({ valid: false, reason: e.response?.data?.error || 'Not found' });
    }
  };

  const redeem = async (card, amount) => {
    try {
      await axios.post(`${API_BASE}/gift-cards/${card._id}/redeem`, {
        amount: Number(amount), redeemedBy: 'admin',
      });
      alert('Redeemed!');
      setLookupResult(null); setLookupCode(''); load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const totalActive = cards.filter(c => c.status === 'active').reduce((a, c) => a + c.balance, 0);
  const totalIssued = cards.reduce((a, c) => a + c.faceValue, 0);

  return (
    <div style={{ padding:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:12 }}>
        <h2 style={{ color: theme.navy, margin:0 }}>🎁 Gift Cards & Vouchers</h2>
        <button onClick={() => setCreating({ faceValue: 1000, issuedTo:'', issuedToPhone:'' })}
          style={{ ...btnStyle, background: theme.primary, color:'white', display:'flex', gap:6, alignItems:'center' }}>
          <Plus size={14}/> Issue Gift Card
        </button>
      </div>

      {/* Quick redeem */}
      <div style={{ ...cardStyle, marginBottom:12 }}>
        <b style={{ color: theme.navy }}>🔍 Look up / Redeem</b>
        <div style={{ display:'flex', gap:8, marginTop:8 }}>
          <input placeholder="Enter gift card code (e.g. GC-ABCD1234)"
            value={lookupCode} onChange={e => setLookupCode(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && lookup()}
            style={{ ...inputStyle, flex:1 }}/>
          <button onClick={lookup} style={{ ...btnStyle, background: theme.secondary, color:'white' }}>
            <Search size={14}/> Look up
          </button>
        </div>
        {lookupResult && (
          <div style={{ marginTop:10, padding:12, background: lookupResult.valid ? '#f0fdf4' : '#fee2e2', borderRadius:8 }}>
            {lookupResult.valid ? (
              <>
                <div><b>Code:</b> {lookupResult.code}</div>
                <div><b>Balance:</b> ₹{lookupResult.balance.toLocaleString('en-IN')} / ₹{lookupResult.faceValue}</div>
                <div><b>Valid till:</b> {lookupResult.expiryDate ? new Date(lookupResult.expiryDate).toLocaleDateString('en-IN') : '—'}</div>
                <div style={{ marginTop:8, display:'flex', gap:6 }}>
                  <input type="number" placeholder="Amount to redeem" id="redeemAmt"
                    style={{ ...inputStyle, width:180 }} defaultValue={lookupResult.balance}/>
                  <button onClick={() => redeem(lookupResult, document.getElementById('redeemAmt').value)}
                    style={{ ...btnStyle, background: theme.success, color:'white' }}>Redeem</button>
                </div>
              </>
            ) : (
              <div style={{ color: theme.danger }}>❌ {lookupResult.reason}</div>
            )}
          </div>
        )}
      </div>

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:12 }}>
        <Stat label="Total Issued" value={`₹${totalIssued.toLocaleString('en-IN')}`} color={theme.secondary}/>
        <Stat label="Active Balance" value={`₹${totalActive.toLocaleString('en-IN')}`} color={theme.success}/>
        <Stat label="Card Count" value={cards.length} color={theme.info}/>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        {['all','active','redeemed','expired','cancelled'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding:'8px 14px', borderRadius:6, cursor:'pointer',
            border:`1px solid ${filter===f ? theme.primary : theme.border}`,
            background: filter===f ? theme.primary : 'white',
            color: filter===f ? 'white' : theme.navy, fontWeight:600, textTransform:'capitalize',
          }}>{f}</button>
        ))}
      </div>

      <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ background:'#f9fafb' }}>
            <th style={th}>Code</th><th style={th}>Face Value</th><th style={th}>Balance</th>
            <th style={th}>Issued To</th><th style={th}>Expiry</th><th style={th}>Status</th>
          </tr></thead>
          <tbody>
            {cards.length === 0 ? <tr><td colSpan={6} style={{ padding:40, textAlign:'center', color:'#888' }}>No gift cards</td></tr>
             : cards.map(c => {
              const s = STATUS_COLOR[c.status] || STATUS_COLOR.cancelled;
              return (
                <tr key={c._id} style={{ borderBottom:'1px solid #f1f3f5' }}>
                  <td style={{ ...td, fontFamily:'monospace', fontWeight:700 }}>{c.code}</td>
                  <td style={td}>₹{c.faceValue.toLocaleString('en-IN')}</td>
                  <td style={{ ...td, color: theme.success, fontWeight:600 }}>₹{c.balance.toLocaleString('en-IN')}</td>
                  <td style={td}>{c.issuedTo || '—'} {c.issuedToPhone ? `(${c.issuedToPhone})` : ''}</td>
                  <td style={td}>{c.expiryDate ? new Date(c.expiryDate).toLocaleDateString('en-IN') : '—'}</td>
                  <td style={td}>
                    <span style={{ padding:'3px 8px', borderRadius:10, fontSize:'0.75rem', fontWeight:700, background: s.bg, color: s.fg, textTransform:'capitalize' }}>{c.status}</span>
                  </td>
                </tr>
              );
             })}
          </tbody>
        </table>
      </div>

      {creating && (
        <div style={modalBg} onClick={() => setCreating(null)}>
          <div style={modalCard} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: theme.navy, margin:'0 0 12px' }}>Issue Gift Card</h3>
            <label style={lbl}>Face value ₹</label>
            <input type="number" value={creating.faceValue} onChange={e => setCreating({ ...creating, faceValue: Number(e.target.value) })} style={{ ...inputStyle, marginBottom:8 }}/>
            <label style={lbl}>Purchased by (patient)</label>
            <select value={creating.purchasedBy || ''} onChange={e => setCreating({ ...creating, purchasedBy: e.target.value || undefined })} style={{ ...inputStyle, marginBottom:8 }}>
              <option value="">— (walk-in / gift) —</option>
              {patients.slice(0, 200).map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
            <label style={lbl}>Issued to (recipient name)</label>
            <input value={creating.issuedTo} onChange={e => setCreating({ ...creating, issuedTo: e.target.value })} style={{ ...inputStyle, marginBottom:8 }}/>
            <label style={lbl}>Recipient Phone (for WhatsApp delivery)</label>
            <input value={creating.issuedToPhone} onChange={e => setCreating({ ...creating, issuedToPhone: e.target.value })} style={{ ...inputStyle, marginBottom:8 }}/>
            <label style={lbl}>Expiry (blank = 1 year)</label>
            <input type="date" value={creating.expiryDate || ''} onChange={e => setCreating({ ...creating, expiryDate: e.target.value })} style={{ ...inputStyle, marginBottom:12 }}/>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => setCreating(null)} style={{ ...btnStyle, flex:1 }}>Cancel</button>
              <button onClick={issue} style={{ ...btnStyle, flex:1, background: theme.primary, color:'white' }}>Issue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div style={{ ...cardStyle, padding:14, borderLeft:`4px solid ${color}` }}>
    <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:1 }}>{label}</div>
    <div style={{ fontSize:22, fontWeight:700, color: theme.navy, marginTop:4 }}>{value}</div>
  </div>
);

const th = { padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:'0.8rem', color:'#555', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 12px', fontSize:'0.85rem', color:'#333' };
const lbl = { fontSize:12, color:'#555', fontWeight:600, marginBottom:4, display:'block' };
const modalBg = { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 };
const modalCard = { background:'white', borderRadius:14, padding:24, maxWidth:500, width:'100%', maxHeight:'90vh', overflowY:'auto' };

export default GiftCardsPage;
